/**
 * Marketing Tracking Utilities
 * Handles Facebook Pixel, Google Analytics, and UTM parameters
 */

// ============================================
// FACEBOOK/META PIXEL SETUP
// ============================================

/**
 * Initialize Facebook Pixel
 * Get your Pixel ID from: https://business.facebook.com/events_manager
 */
export const initFacebookPixel = (pixelId) => {
  if (!pixelId || typeof window === 'undefined') return;

  // Check if pixel is already loaded
  if (window.fbq) return;

  // Facebook Pixel Code
  /* eslint-disable no-unused-expressions */
  !(function(f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable no-unused-expressions */

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

};

/**
 * Track Facebook Pixel events
 */
export const trackFacebookEvent = (eventName, data = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, data);
  }
};

// ============================================
// GOOGLE ANALYTICS SETUP
// ============================================

/**
 * Initialize Google Analytics (GA4)
 * Get your Measurement ID from: https://analytics.google.com
 */
export const initGoogleAnalytics = (measurementId) => {
  if (!measurementId || typeof window === 'undefined') return;

  // Check if GA is already loaded
  if (window.gtag) return;

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', measurementId);

};

/**
 * Track Google Analytics events
 */
export const trackGoogleEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// ============================================
// UTM PARAMETERS
// ============================================

/**
 * Get UTM parameters from URL
 * Example: ?utm_source=instagram&utm_campaign=25euro
 */
export const getUTMParameters = () => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    utm_source: urlParams.get('utm_source') || null,      // e.g., "instagram"
    utm_medium: urlParams.get('utm_medium') || null,      // e.g., "cpc" or "social"
    utm_campaign: urlParams.get('utm_campaign') || null,  // e.g., "25euro_promo"
    utm_content: urlParams.get('utm_content') || null,    // e.g., "ad_variant_a"
    utm_term: urlParams.get('utm_term') || null,          // e.g., "studio_booking"
  };
};

/**
 * Save UTM parameters to localStorage
 * This helps attribute conversions even if user comes back later
 */
export const saveUTMParameters = () => {
  const utmParams = getUTMParameters();
  
  // Only save if we have at least one UTM parameter
  if (Object.values(utmParams).some(val => val !== null)) {
    localStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
};

/**
 * Get saved UTM parameters
 */
export const getSavedUTMParameters = () => {
  const saved = localStorage.getItem('utm_params');
  return saved ? JSON.parse(saved) : {};
};

// ============================================
// CONVERSION TRACKING
// ============================================

/**
 * Track email signup conversion
 * This is your main conversion event!
 */
export const trackEmailSignup = (email, listingStudio = false) => {
  const utmParams = getSavedUTMParameters();
  
  // Track on Facebook Pixel
  trackFacebookEvent('Lead', {
    content_name: '25 Euro Discount',
    value: 25.00,
    currency: 'EUR',
    ...utmParams
  });

  // Track on Google Analytics
  trackGoogleEvent('generate_lead', {
    event_category: 'Conversion',
    event_label: 'Email Signup',
    value: 25.00,
    listing_studio: listingStudio,
    ...utmParams
  });

  // You can also send to your own backend API
  sendToBackend({
    email,
    listingStudio,
    utmParams,
    timestamp: new Date().toISOString()
  });
};

/**
 * Track page view with UTM data
 */
export const trackPageView = () => {
  const utmParams = getUTMParameters();
  
  if (window.gtag) {
    trackGoogleEvent('page_view', utmParams);
  }
  
  if (window.fbq) {
    trackFacebookEvent('ViewContent', utmParams);
  }
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName) => {
  trackGoogleEvent('button_click', {
    event_category: 'Engagement',
    event_label: buttonName
  });
};

// ============================================
// BACKEND API (leads)
// ============================================

/**
 * POST JSON to your server. Set REACT_APP_LEADS_API_URL in .env.local to a
 * full URL (e.g. https://api.example.com/leads). Same endpoint receives
 * { type: 'email_signup', ... } from the hero and { type: 'studio_listing_inquiry', ... } from the modal.
 */
const getLeadsApiUrl = () => (process.env.REACT_APP_LEADS_API_URL || '').trim();
const getCampaignCaptureUrl = () =>
  (
    process.env.REACT_APP_CAMPAIGN_CAPTURE_URL ||
    'https://ptvmjdut2byts632jwhd2hyu440rwmlb.lambda-url.eu-central-1.on.aws'
  ).trim();

const sendToBackend = async (data) => {
  const url = getLeadsApiUrl();
  if (!url) return;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type: 'email_signup', ...data }),
    });
    if (!response.ok) {
      console.warn('Leads API (email_signup):', response.status, await response.text().catch(() => ''));
    }
  } catch (error) {
    console.error('Failed to send email signup lead:', error);
  }
};

/**
 * Studio modal: POST after Abschicken. If REACT_APP_LEADS_API_URL is empty, logs + returns (no throw).
 * Throws on network failure or non-OK HTTP response when URL is set.
 */
export const submitStudioLead = async (heroEmail, { studioname, email, telefon }) => {
  const url = getLeadsApiUrl();
  const payload = {
    type: 'studio_listing_inquiry',
    heroEmail,
    studioname,
    email,
    telefon,
    ...getSavedUTMParameters(),
    submittedAt: new Date().toISOString(),
  };
  if (!url) {
    console.warn(
      'submitStudioLead: REACT_APP_LEADS_API_URL nicht gesetzt — Studio-Anfrage wird nicht per POST gesendet.',
      payload
    );
    return;
  }
  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Netzwerkfehler. Bitte später erneut versuchen.');
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `Server antwortet mit ${response.status}`);
  }
  return response.json().catch(() => ({}));
};

export const submitPromoLead = async ({
  email,
  agbAccepted,
  isStudio,
  studioName,
  phoneNumber,
  captchaToken,
}) => {
  const payload = {
    email,
    agbAccepted,
    isStudio,
    studioName: isStudio ? studioName : undefined,
    phoneNumber: isStudio && phoneNumber ? phoneNumber : undefined,
    captchaToken: captchaToken ? captchaToken : undefined,
    source: 'promo-page',
    utmSource: getUTMParameters().utm_source,
    utmMedium: getUTMParameters().utm_medium,
    utmCampaign: getUTMParameters().utm_campaign,
    utmContent: getUTMParameters().utm_content,
  };

  let response;
  try {
    response = await fetch(getCampaignCaptureUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Netzwerkfehler. Bitte später erneut versuchen.');
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Bitte versuche es später erneut.');
    }
    if (response.status === 403 || response.status === 500) {
      throw new Error('Aktuell ist etwas schiefgelaufen. Bitte später erneut versuchen.');
    }
    throw new Error(data.error || 'Die Anmeldung konnte nicht gespeichert werden.');
  }

  return data;
};

/**
 * Marketing: fire when the studio modal form is successfully submitted (after API OK).
 */
export const trackStudioLeadComplete = () => {
  const utmParams = getSavedUTMParameters();
  trackFacebookEvent('Lead', {
    content_name: 'Studio Listing Inquiry',
    value: 25.0,
    currency: 'EUR',
    ...utmParams,
  });
  trackGoogleEvent('generate_lead', {
    event_category: 'Conversion',
    event_label: 'Studio Modal Complete',
    value: 25.0,
    listing_studio: true,
    ...utmParams,
  });
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get or create a unique visitor ID
 */
export const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  
  if (!visitorId) {
    visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitor_id', visitorId);
  }
  
  return visitorId;
};

/**
 * Track time on page
 */
export const trackTimeOnPage = () => {
  const startTime = Date.now();
  
  window.addEventListener('beforeunload', () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000); // seconds
    
    trackGoogleEvent('time_on_page', {
      event_category: 'Engagement',
      value: timeSpent
    });
  });
};

